import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Spinner, Modal } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import swal from 'sweetalert';
import { User as UserIcon, Shield, Building2, Eye, EyeOff, UserCog} from 'lucide-react';

import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import { uploadAvatar, deleteAvatar } from '../../../services/AvatarService';
import { leaveCompany as leaveCompanyApi } from '../../../services/CompanyMembershipService';
import { fetchLoginEvents } from '../../../services/LoginHistoryService';
import { setUserAvatar, setUserProfile, removeUserCompany, logout } from '../../../store/actions/AuthActions';
import DeleteCompanyModal from '../Common/DeleteCompanyModal';
import { sendPasswordReset, addPasswordToCurrentUser, getCurrentUserProviders } from '../../../services/AuthService';
import PageHeader from '../../layouts/PageHeader';
import SettingsRail from './SettingsRail';
import useHashScroll from './useHashScroll';
import ProfileSection from './user-sections/ProfileSection';
import SecuritySection from './user-sections/SecuritySection';
import CompaniesSection from './user-sections/CompaniesSection';
import OwnershipSection from './OwnershipSection';

// Single source of truth for the user-settings sections — drives both the
// shared SettingsRail (labels/icons) and URL routing (slug). No `group`, so the
// rail renders these as a flat list.
const SECTIONS = [
    { slug: 'profile',   label: 'Profil',    Icon: UserIcon },
    { slug: 'security',  label: 'Säkerhet',  Icon: Shield },
    { slug: 'companies', label: 'Företag',   Icon: Building2 },
];
const DEFAULT_SECTION = 'profile';

const UserSettings = () => {
    const { companyId, userId: requestedUserId, section } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const reduxUser = useSelector((state) => state.auth.user);
    // Personal settings always belong to the signed-in account. A company
    // administrator's edited/deep-linked URL cannot select another profile.
    const userId = reduxUser?.uid;
    const currentCompanyId = useSelector((state) => state.auth.selectedCompanyId);

    // Active section is derived from the :section URL param (deep-linkable),
    // falling back to the default until the redirect below canonicalizes the URL.
    const activeSection = SECTIONS.find((s) => s.slug === section)?.slug || DEFAULT_SECTION;
    const [loading, setLoading] = useState(false);
    const [savingProfile, setSavingProfile] = useState(false);
    const [avatarUploading, setAvatarUploading] = useState(false);
    const [passwordBusy, setPasswordBusy] = useState(false);
    const [leavingCompanyId, setLeavingCompanyId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [signInMethods, setSignInMethods] = useState(null);
    const [loginEvents, setLoginEvents] = useState(null);
    const [loginEventsLoading, setLoginEventsLoading] = useState(false);
    const [setPasswordModal, setSetPasswordModal] = useState({ show: false });
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [passwordError, setPasswordError] = useState(null);

    const [personalInfo, setPersonalInfo] = useState({
        firstName: '',
        lastName: '',
        phoneNumber: '',
    });
    const [associatedCompanies, setAssociatedCompanies] = useState([]);
    const fileInputRef = useRef(null);

    const currentAvatar = reduxUser?.avatarUrl || reduxUser?.photoUrl || null;
    const userInitials = (
        (reduxUser?.firstName?.[0] || '') + (reduxUser?.lastName?.[0] || '')
    ).toUpperCase() || '?';

    // Canonicalize the URL: a bare .../settings or an unknown section slug
    // redirects to the default section.
    useEffect(() => {
        if (userId && (requestedUserId !== userId || !SECTIONS.some((s) => s.slug === section))) {
            history.replace(`/${companyId}/user/${userId}/settings/${activeSection}`);
        }
    }, [section, activeSection, companyId, userId, requestedUserId, history]);

    // Deep-link within a section: scroll to the #anchor once it has rendered.
    useHashScroll([activeSection]);

    useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                // The `users/:userId/companies` fetch is intentionally kept
                // for the cross-company list view further down ("you have
                // access to these N companies"). Capabilities for the
                // ACTIVE company are owned by useCapabilities() in
                // src/context/CapabilityContext.js — Phase 1.2 of the
                // access-control redesign. Don't reach for `company.roles`
                // for self-checks in the active company; ask the hook.
                const [profileRes, companiesRes] = await Promise.all([
                    axios.get(getApiUrl(`users/${userId}/profile`)),
                    axios.get(getApiUrl(`users/${userId}/companies`)),
                ]);
                if (cancelled) return;
                const profile = profileRes.data.profile || {};
                setPersonalInfo({
                    firstName: profile.first_name || '',
                    lastName: profile.last_name || '',
                    phoneNumber: profile.phone_number || '',
                });
                // Sync avatar_url from DB into Redux (handles page reloads
                // where Redux was rehydrated via refresh-token and doesn't
                // yet include avatar_url)
                if (profile.avatar_url && profile.avatar_url !== reduxUser?.avatarUrl) {
                    dispatch(setUserAvatar(profile.avatar_url));
                }
                setAssociatedCompanies(companiesRes.data.companies || []);
            } catch (err) {
                // Keep defaults; errors surfaced on save
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [userId]);

    const refreshSignInMethods = async () => {
        try {
            setSignInMethods(await getCurrentUserProviders());
        } catch {
            setSignInMethods(null);
        }
    };

    const handleLogoutAll = async () => {
        const confirmed = await swal({
            title: 'Logga ut från alla webbläsare?',
            text: 'Du behöver logga in igen på alla enheter, även den här.',
            buttons: ['Avbryt', 'Logga ut'],
        });
        if (!confirmed) return;
        const result = await dispatch(logout(history, { everywhere: true }));
        if (!result.success) swal('Du är utloggad här, men vi kunde inte bekräfta utloggningen på servern. Logga in och försök igen.', { icon: 'error' });
    };

    useEffect(() => {
        refreshSignInMethods();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reduxUser?.email]);

    useEffect(() => {
        if (activeSection !== 'security' || !userId || loginEvents !== null) return;
        let cancelled = false;
        (async () => {
            setLoginEventsLoading(true);
            try {
                const events = await fetchLoginEvents(userId, { limit: 50 });
                if (!cancelled) setLoginEvents(events);
            } catch {
                if (!cancelled) setLoginEvents([]);
            } finally {
                if (!cancelled) setLoginEventsLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSection, userId]);

    const handlePersonalInfoChange = (e) => {
        setPersonalInfo({ ...personalInfo, [e.target.name]: e.target.value });
    };

    const handlePersonalInfoSubmit = async (e) => {
        e.preventDefault();
        setSavingProfile(true);
        try {
            await axios.put(getApiUrl(`users/${userId}/profile`), {
                first_name: personalInfo.firstName,
                last_name: personalInfo.lastName,
                phone_number: personalInfo.phoneNumber,
            });
            dispatch(setUserProfile({
                firstName: personalInfo.firstName,
                lastName: personalInfo.lastName,
                phoneNumber: personalInfo.phoneNumber,
            }));
            swal('Profil uppdaterad.', { icon: 'success' });
        } catch (err) {
            swal('Kunde inte uppdatera profilen.', { icon: 'error' });
        } finally {
            setSavingProfile(false);
        }
    };

    const handleAvatarSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
            swal('Endast JPEG, PNG eller WebP tillåts.', { icon: 'error' });
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            swal('Bilden måste vara mindre än 2 MB.', { icon: 'error' });
            return;
        }
        setAvatarUploading(true);
        try {
            const { avatarUrl } = await uploadAvatar(userId, file);
            dispatch(setUserAvatar(avatarUrl));
        } catch (err) {
            const msg = err?.response?.data?.message || 'Kunde inte ladda upp bilden.';
            swal(msg, { icon: 'error' });
        } finally {
            setAvatarUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleAvatarRemove = async () => {
        if (!currentAvatar) return;
        const confirmed = await swal({
            title: 'Ta bort profilbild?',
            buttons: ['Avbryt', 'Ta bort'],
            dangerMode: true,
        });
        if (!confirmed) return;
        setAvatarUploading(true);
        try {
            await deleteAvatar(userId);
            dispatch(setUserAvatar(null));
        } catch (err) {
            swal(err?.response?.data?.message || 'Kunde inte ta bort bilden.', { icon: 'error' });
        } finally {
            setAvatarUploading(false);
        }
    };

    const openSetPasswordModal = () => {
        setNewPassword('');
        setConfirmPassword('');
        setShowPassword(false);
        setShowConfirm(false);
        setPasswordError(null);
        setSetPasswordModal({ show: true });
    };

    const closeSetPasswordModal = () => {
        if (passwordBusy) return;
        setSetPasswordModal({ show: false });
    };

    const submitSetPassword = async () => {
        setPasswordError(null);
        if (newPassword.length < 6) {
            setPasswordError('Lösenordet måste vara minst 6 tecken.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError('Lösenorden matchar inte.');
            return;
        }
        setPasswordBusy(true);
        try {
            await addPasswordToCurrentUser(newPassword);
            setSetPasswordModal({ show: false });
            await dispatch(logout(history));
            swal('Lösenordet har lagts till. Logga in igen för att fortsätta.', { icon: 'success' });
        } catch (err) {
            setPasswordError(err?.message || 'Kunde inte aktivera lösenordsinloggning.');
        } finally {
            setPasswordBusy(false);
        }
    };

    const handleCompanyDeleted = (deletedId) => {
        setAssociatedCompanies((prev) => prev.filter((c) => c.company_id !== deletedId));
        dispatch(removeUserCompany(deletedId));
        if (currentCompanyId === deletedId) {
            localStorage.removeItem('selectedCompany');
            history.push('/select-company');
        }
    };

    const handleLeaveCompany = async (company) => {
        const companyId = company.company_id;
        const confirmed = await swal({
            title: `Lämna ${company.company_name}?`,
            text: 'Du kommer inte längre ha åtkomst till företagets data. Du kan bli inbjuden igen senare av en administratör.',
            buttons: ['Avbryt', 'Lämna företaget'],
            dangerMode: true,
        });
        if (!confirmed) return;

        setLeavingCompanyId(companyId);
        try {
            await leaveCompanyApi(userId, companyId);
            setAssociatedCompanies((prev) => prev.filter((c) => c.company_id !== companyId));
            dispatch(removeUserCompany(companyId));
            if (currentCompanyId === companyId) {
                localStorage.removeItem('selectedCompany');
                history.push('/select-company');
                return;
            }
            swal('Du har lämnat företaget.', { icon: 'success' });
        } catch (err) {
            const msg = err?.response?.data?.message || 'Kunde inte lämna företaget.';
            swal(msg, { icon: 'error' });
        } finally {
            setLeavingCompanyId(null);
        }
    };

    const handleSendPasswordReset = async () => {
        if (!reduxUser?.email) {
            swal('Ingen e-postadress hittades för ditt konto.', { icon: 'error' });
            return;
        }
        setPasswordBusy(true);
        try {
            await sendPasswordReset(reduxUser.email);
            swal('Din begäran har tagits emot. Om adressen kan användas för återställning får du en länk via e-post.', { icon: 'success' });
        } catch (err) {
            if (err?.code === 'auth/google-only-user') {
                setPasswordBusy(false);
                openSetPasswordModal();
                return;
            }
            swal(err?.message || 'Kunde inte skicka återställningslänk.', { icon: 'error' });
        } finally {
            setPasswordBusy(false);
        }
    };

    const renderSetPasswordModal = () => {
        const wrapperStyle = { position: 'relative', display: 'flex', alignItems: 'center' };
        const inputStyle = { paddingRight: '2.75rem' };
        const eyeStyle = {
            position: 'absolute',
            right: '0.75rem',
            color: '#9ca3af',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
        };

        return (
            <Modal show={setPasswordModal.show} onHide={closeSetPasswordModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1.1rem' }}>Aktivera lösenordsinloggning</Modal.Title>
                </Modal.Header>
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        submitSetPassword();
                    }}
                >
                    <Modal.Body>
                        <p style={{ fontSize: '0.875rem', color: '#737B8B', marginBottom: '1rem' }}>
                            Du behåller Google-inloggningen och får e-post ({reduxUser?.email}) + lösenord som tillägg.
                        </p>

                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Nytt lösenord</Form.Label>
                            <div style={wrapperStyle}>
                                <Form.Control
                                    type={showPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Minst 6 tecken"
                                    autoFocus
                                    autoComplete="new-password"
                                    disabled={passwordBusy}
                                    style={inputStyle}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    tabIndex={-1}
                                    aria-label={showPassword ? 'Dölj lösenord' : 'Visa lösenord'}
                                    style={eyeStyle}
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-0">
                            <Form.Label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Bekräfta lösenord</Form.Label>
                            <div style={wrapperStyle}>
                                <Form.Control
                                    type={showConfirm ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Skriv samma lösenord igen"
                                    autoComplete="new-password"
                                    disabled={passwordBusy}
                                    isInvalid={!!confirmPassword && confirmPassword !== newPassword}
                                    style={inputStyle}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirm((v) => !v)}
                                    tabIndex={-1}
                                    aria-label={showConfirm ? 'Dölj lösenord' : 'Visa lösenord'}
                                    style={eyeStyle}
                                >
                                    {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </Form.Group>

                        {passwordError && (
                            <p style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.75rem', marginBottom: 0 }}>
                                {passwordError}
                            </p>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={closeSetPasswordModal} disabled={passwordBusy}>
                            Avbryt
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={passwordBusy || !newPassword || !confirmPassword}
                        >
                            {passwordBusy ? 'Aktiverar...' : 'Aktivera'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        );
    };

    if (loading || !userId) {
        return (
            <>
                <PageHeader
                  icon={UserCog}
                  title="Användarinställningar"
                  subtitle="Ditt konto, dina uppgifter och din inloggning."
                />
                <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                    <Spinner animation="border" size="sm" /> <span className="ms-2">Laddar...</span>
                </div>
            </>
        );
    }

    return (
        <>
            <PageHeader
              icon={UserCog}
              title="Användarinställningar"
              subtitle="Ditt konto, dina uppgifter och din inloggning."
            />
            <div className="settings-layout">
                <SettingsRail
                    basePath={`/${companyId}/user/${userId}/settings`}
                    activeSlug={activeSection}
                    sections={SECTIONS}
                />

                <div className="settings-content">
                    {activeSection === 'profile' && (
                        <ProfileSection
                            currentAvatar={currentAvatar}
                            userInitials={userInitials}
                            fileInputRef={fileInputRef}
                            avatarUploading={avatarUploading}
                            handleAvatarSelect={handleAvatarSelect}
                            handleAvatarRemove={handleAvatarRemove}
                            personalInfo={personalInfo}
                            handlePersonalInfoChange={handlePersonalInfoChange}
                            handlePersonalInfoSubmit={handlePersonalInfoSubmit}
                            savingProfile={savingProfile}
                            email={reduxUser?.email}
                        />
                    )}

                    {activeSection === 'security' && (
                        <SecuritySection
                            signInMethods={signInMethods}
                            email={reduxUser?.email}
                            passwordBusy={passwordBusy}
                            handleSendPasswordReset={handleSendPasswordReset}
                            openSetPasswordModal={openSetPasswordModal}
                            loginEvents={loginEvents}
                            loginEventsLoading={loginEventsLoading}
                            handleLogoutAll={handleLogoutAll}
                        />
                    )}

                    {activeSection === 'companies' && (
                        <>
                        <CompaniesSection
                            associatedCompanies={associatedCompanies}
                            leavingCompanyId={leavingCompanyId}
                            handleLeaveCompany={handleLeaveCompany}
                            setDeleteTarget={setDeleteTarget}
                        />
                        <OwnershipSection key={companyId} companyId={companyId} userId={userId} />
                        </>
                    )}
                </div>
            </div>

            <DeleteCompanyModal
                show={!!deleteTarget}
                companyId={deleteTarget?.company_id}
                companyName={deleteTarget?.company_name}
                onHide={() => setDeleteTarget(null)}
                onDeleted={handleCompanyDeleted}
            />

            {renderSetPasswordModal()}
        </>
    );
};

export default UserSettings;
